import IsLoading from "@/components/layouts/IsLoading";
import TrialContext from "@/contexts/data/TrialContext";
import useTrialStatuses from "@/hooks/data/useTrialStatuses";
import { Alert, AlertTitle, Box, Button, Chip, CircularProgress, Divider, Grid, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Theme, Tooltip, TooltipProps, Typography, darken, lighten, styled, tooltipClasses, useTheme } from "@mui/material";
import { FC, Fragment, ReactNode, useCallback, useContext, useDeferredValue, useMemo, useState } from "react";
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Link, Outlet, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClipboardList, faCommentAlt, faCommentCaptions, faExclamationCircle, faFileCheck, faFileMagnifyingGlass, faL, faN, faS } from "@fortawesome/pro-duotone-svg-icons";
import { green, grey, red, yellow } from "@mui/material/colors";
import useParticipants from "@/hooks/data/useParticipants";
import useParticipantStatuses from "@/hooks/data/useParticipantStatuses";
import InstitutionContext from "@/contexts/data/InstitutionContext";
import Show from "@/components/layouts/Show";
import { AppSettings } from "@/AppSettings";
import { CoordinatingGroup, Institution, MasterGroup, Participant, UpdateStatusDataType, UpdateStatusEvent, UpdateStatusEventType } from "@/api/dtos";
import { DefaultSortableFields, IStatusListSortable } from "@/components/lists/status/StatusListSort";
import { DateTime } from 'luxon';
import InstitutionsContext from "@/contexts/data/InstitutionsContext";
import SetLinks from "@/components/layouts/SetLinks";
import { ParticipantInstitutionCreateLink, ParticipantTrialCreateLink } from "@/contexts/layouts/LinkContext";
import useStatusUpdate from "@/hooks/data/useStatusUpdate";
import MasterGroupsContext from "@/contexts/data/MasterGroupsContext";
import CoordinatingGroupsContext from "@/contexts/data/CoordinatingGroupsContext";
import ParticipantsContext from "@/contexts/data/ParticipantsContext";
import Text, { ITextProps } from "@/components/forms/inputs/Text";
import Multiselect, { IMultiselectProps } from "@/components/forms/inputs/Multiselect";
import ParticipantStatusList from "@/components/lists/status/ParticipantStatusList";
import ParticipantContext from "@/contexts/data/ParticipantContext";
import useConfigurationSourceDataMap from "@/hooks/data/useConfigurationSourceDataMap";
import useParticipantSourceDataAllocation from "@/hooks/data/useParticipantSourceDataAllocation";
import ConfigurationSourceDataContext from "@/contexts/data/ConfigurationSourceDataContext";
import useSourceData from "@/hooks/data/useSourceData";
import SourceDataContext from "@/contexts/data/SourceDataContext";
import SourceDataNavigation from "@/components/forms/forms/source-data/SourceDataNavigation";
import { LoadingButton } from "@mui/lab";
import useParticipantSourceDataEventStatuses from "@/hooks/data/useParticipantSourceDataEventStatuses";
import useParticipantSourceDataFormStatuses from "@/hooks/data/useParticipantSourceDataFormStatuses";
import ParticipantSourceDataFormStatusesContext from "@/contexts/data/ParticipantSourceDataFormStatusesContext";
import ParticipantSourceDataEventStatusesContext from "@/contexts/data/ParticipantSourceDataEventStatusesContext";
import { StatusListItemTooltip } from "@/components/lists/status/StatusListStats";
import SourceDataFormContext from "@/contexts/form/SourceDataFormContext";
import useParticipantNotesAndQueries from "@/hooks/data/useParticipantNotesAndQueries";
import ParticipantNotesAndQueriesContext from "@/contexts/data/ParticipantNotesAndQueriesContext";
import asyncDebounce from "@/utilities/asyncDebounce";
import { debounce } from "lodash-es";
import ParticipantSourceDataFindingsContext from "@/contexts/data/ParticipantSourceDataFindingsContext";
import useParticipantSourceDataFindings from "@/hooks/data/useParticipantSourceDataFindings";
import { MessagesView } from "@/components/layouts/Messages";
import useEvent from "@/hooks/form/source-data/useEvent";
import MetadataContext from "@/contexts/data/MetadataContext";
import MasterGroupContext from "@/contexts/data/MasterGroupContext";
import CoordinatingGroupContext from "@/contexts/data/CoordinatingGroupContext";
import TrialConfigurationSourceDataContext from "@/contexts/data/TrialConfigurationSourceDataContext";
import useForm from "@/hooks/form/source-data/useForm";
import { ShowLayoutContext } from "@/contexts/layouts/ShowLayoutContext";
import SourceDataFormSettingsContext, { DefaultSourceDataFormSettings, ISourceDataFormSettings } from "@/contexts/form/SourceDataFormSettingsContext";
import { useLocalStorage } from "react-use";
import SourceDataFormSettingsDialog from "@/components/dialogs/SourceDataFormSettingsDialog";
import SourceDataEventEditDialog from "@/components/dialogs/SourceDataEventEditDialog";
import { LoadingContext } from "@/contexts/layouts/LoadingContext";

const TitleBox = styled(Box)(({theme}) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
}))

const ParticipantSourceDataMessages: FC = () => {
    const { data: trial } = useContext(TrialContext);
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: coordinatingGroup } = useContext(CoordinatingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: participant } = useContext(ParticipantContext);
    const { data: metadata } = useContext(MetadataContext);
    const { data: trialConfiguration } = useContext(TrialConfigurationSourceDataContext);
    const { data: configuration } = useContext(ConfigurationSourceDataContext);
    const { data: sourceData } = useContext(SourceDataContext);

    const { studyEventOid, studyEventRepeat, caseReportFormOid, itemName } = useParams();

    const event = useEvent(
        metadata,
        trialConfiguration,
        configuration,
        sourceData,
        trial,
        masterGroup,
        coordinatingGroup,
        institution,
        participant,
        studyEventOid,
        +studyEventRepeat!
    )

    const form = useForm(
        metadata,
        trialConfiguration,
        configuration,
        sourceData,
        trial,
        masterGroup,
        coordinatingGroup,
        institution,
        participant,
        studyEventOid,
        +studyEventRepeat!,
        caseReportFormOid
    )
    
    return (
        <>
            <MessagesView
                entityName="Allocation"
                storageKey={`Alert-Configuration-${participant?.id}-${configuration?.configuration?.id}-${configuration?.configuration?.modifiedDate}`}
                alertSuccess={configuration?.configuration?.alertSuccess}
                alertInformation={configuration?.configuration?.alertInformation}
                alertWarning={configuration?.configuration?.alertWarning}
                alertError={configuration?.configuration?.alertError}
            />
            {
                !!event && (
                    <MessagesView
                        entityName="Event"
                        storageKey={`Alert-Event-${participant?.id}-${event.studyEventOid}-${configuration?.configuration?.id}-${configuration?.configuration?.modifiedDate}`}
                        alertSuccess={event.configuration?.alertSuccess}
                        alertInformation={event.configuration?.alertInformation}
                        alertWarning={event.configuration?.alertWarning}
                        alertError={event.configuration?.alertError}
                    />
                )
            }
            {
                !!form && (
                    <MessagesView
                        entityName="Form"
                        storageKey={`Alert-Form-${participant?.id}-${form.studyEventOid}-${form.caseReportFormOid}-${configuration?.configuration?.id}-${configuration?.configuration?.modifiedDate}`}
                        alertSuccess={form.configuration?.alertSuccess}
                        alertInformation={form.configuration?.alertInformation}
                        alertWarning={form.configuration?.alertWarning}
                        alertError={form.configuration?.alertError}
                    />
                )
            }
            {
                !!form?.formVersion && (
                    <MessagesView
                        entityName="Form Version"
                        storageKey={`Alert-Form-Version-${participant?.id}-${form.studyEventOid}-${form.caseReportFormOid}-${form.formVersion.caseReportFormVersionOid}-${configuration?.configuration?.id}-${configuration?.configuration?.modifiedDate}`}
                        alertSuccess={form.formVersion.configuration?.alertSuccess}
                        alertInformation={form.formVersion.configuration?.alertInformation}
                        alertWarning={form.formVersion.configuration?.alertWarning}
                        alertError={form.formVersion.configuration?.alertError}
                    />
                )
            }
        </>
    );
}

const ParticipantSourceData: FC = () => {
    const { data: trial } = useContext(TrialContext);
    const { data: participant } = useContext(ParticipantContext);

    const { data: allocation, loading: allocationLoading, validating: allocationValidating } = useParticipantSourceDataAllocation(trial?.id && participant?.id ? { trialId: trial.id, participantId: participant.id } : undefined);
    const configurationContext = useConfigurationSourceDataMap(trial?.id, allocation?.configurationSourceDataId ? allocation?.configurationSourceDataId : undefined);

    const sourceDataContext = useSourceData(trial?.id, participant?.id);

    const { data: sourceData, loading: sourceDataLoading, validating: sourceDataValidating, refresh: sourceDataRefresh } = sourceDataContext;
    const { data: configuration, loading: configurationLoading, validating: configurationValidating } = configurationContext;

    const eventStatusContext =  useParticipantSourceDataEventStatuses(participant?.id);
    const { loading: eventStatusesLoading, validating: eventStatusesValidating, refresh: eventStatusRefresh } = eventStatusContext;

    

    const formStatusContext =  useParticipantSourceDataFormStatuses(participant?.id);
    const { loading: formStatusesLoading, validating: formStatusesValidating, refresh: formStatusRefresh } = formStatusContext;

    const notesContext = useParticipantNotesAndQueries(participant?.id);
    const { loading: notesLoading, validating: notesValidating, refresh: notesRefresh } = notesContext;

    const findingsContext = useParticipantSourceDataFindings(participant?.id);
    const { loading: findingsLoading, validating: findingsValidating, refresh: findingsRefresh } = findingsContext;

    const [refreshing, setRefreshing] = useState(false);

    const { studyEventOid, studyEventRepeat, caseReportFormOid, trialOid, institutionCode, studyNumber, itemName } = useParams()


    const debouncedEventStatusRefresh = useCallback(debounce(async () => {
        await eventStatusRefresh();
    }, 500, { trailing: true, leading: false}), [eventStatusRefresh]);

    const debouncedFormStatusRefresh = useCallback(debounce(async () => {
        await formStatusRefresh();
    }, 500, { trailing: true, leading: false}), [formStatusRefresh]);

    const debouncedSourceDataRefresh = useCallback(debounce(async () => {
        await sourceDataRefresh();
    }, 500, { trailing: true, leading: false}), [sourceDataRefresh]);


    const afterUpdateStatus = useCallback(async (event: UpdateStatusEvent) => {
        if (event.trialId == trial?.id &&
            event.participantId == participant?.id &&
            event.type === UpdateStatusEventType.Complete) {
                const promises: Promise<any>[] = [];

                if (
                    event.dataType === UpdateStatusDataType.Reviews ||
                    event.dataType === UpdateStatusDataType.Verifications ||
                    event.dataType === UpdateStatusDataType.ReviewsAndVerifications ||
                    event.dataType === UpdateStatusDataType.All
                )
                {
                    promises.push(eventStatusRefresh() as any);
                    promises.push(formStatusRefresh() as any);
                }
                
                if (event.dataType === UpdateStatusDataType.All)
                {
                    promises.push(debouncedSourceDataRefresh() as any);
                }

                await Promise.all(promises);
                setRefreshing(false);
        }
    }, [eventStatusRefresh, formStatusRefresh, setRefreshing, debouncedSourceDataRefresh, trial, participant]);


    const onQueueUpdate = useCallback(async () => {
        setRefreshing(true);
    }, [ setRefreshing]);

    const {processing, queueUpdate, error} = useStatusUpdate(
        onQueueUpdate,
        afterUpdateStatus
    );

    const queueStatusUpdate = useCallback(async () => {
        setRefreshing(true);

        await queueUpdate({ 
            participantId: participant?.id
        });
    }, [queueUpdate, participant?.id, setRefreshing]);

    const sourceDataFormContext = useMemo(() => {
        return {
            updating: processing || refreshing,
            queueUpdate: queueStatusUpdate
        }
    }, [queueStatusUpdate, processing, refreshing])

    const loading = configurationLoading || allocationLoading || sourceDataLoading || eventStatusesLoading || formStatusesLoading;
    const validating = configurationValidating || allocationValidating || sourceDataValidating || eventStatusesValidating || formStatusesValidating || processing || refreshing || notesLoading || notesValidating || findingsLoading || findingsValidating;
    const { showLayout } = useContext(ShowLayoutContext);

    const [formSettings, setFormSettings] = useLocalStorage<ISourceDataFormSettings>('source-data-form-settings', DefaultSourceDataFormSettings)

    const formSettingsContext = useMemo(() => {
        return {
            settings: formSettings ?? DefaultSourceDataFormSettings,
            setSettings: setFormSettings as any
        }
    }, [formSettings, setFormSettings]);

    const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
    const [editEventDialogOpen, setEditEventDialogOpen] = useState(false);
    
    const { loading: siteLoading } = useContext(LoadingContext)

    return (
        <>
            <SetLinks createTrialLink={ParticipantTrialCreateLink} createInstitutionLink={ParticipantInstitutionCreateLink} />
            <IsLoading name="participant-source-data-page" loading={loading} validating={validating} />
            <ParticipantSourceDataFindingsContext.Provider value={findingsContext}>
                <ParticipantNotesAndQueriesContext.Provider value={notesContext}>
                    <SourceDataContext.Provider value={sourceDataContext}>
                        <SourceDataFormContext.Provider value={sourceDataFormContext}>
                            <ConfigurationSourceDataContext.Provider value={configurationContext}>
                                <ParticipantSourceDataEventStatusesContext.Provider value={eventStatusContext}>
                                    <ParticipantSourceDataFormStatusesContext.Provider value={formStatusContext}>
                                        <SourceDataFormSettingsContext.Provider value={formSettingsContext}>
                                            {
                                                !AppSettings.showLayout && (siteLoading || loading) && (
                                                    <Box
                                                        sx={{
                                                            position: 'fixed',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        <CircularProgress size={80} />
                                                    </Box>
                                                )
                                            }
                                            <Show show={!siteLoading && !loading}>
                                                <ParticipantSourceDataMessages />
                                                <TitleBox>
                                                    <Box>

                                                        <Typography variant="h1">
                                                            {participant?.studyNumber}
                                                        </Typography>
                                                        <Box
                                                            sx={{
                                                                fontSize: '2rem'
                                                            }}
                                                        >
                                                            Allocation: {configuration?.configuration?.name}
                                                        </Box>
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 2
                                                        }}
                                                    >
                                                        {
                                                            showLayout && studyEventOid && caseReportFormOid && !itemName && (
                                                                <StatusListItemTooltip 
                                                                    title={
                                                                        <>
                                                                            <strong>Grid View</strong>
                                                                        </>
                                                                    }
                                                                >
                                                                    <Paper>
                                                                        <Button
                                                                            color="inherit"
                                                                            component={Link}
                                                                            to={`/${trialOid}${institutionCode ? `/institutions/${institutionCode}` : ''}/participants/${studyNumber}/forms/${caseReportFormOid}`}
                                                                            sx={{
                                                                                minWidth: 0
                                                                            }}
                                                                        >
                                                                            <FontAwesomeIcon icon={AppSettings.icons.itemGrid} size="lg"  />
                                                                        </Button>
                                                                    </Paper>
                                                                </StatusListItemTooltip>
                                                            )
                                                        }
                                                        {
                                                            studyEventOid && caseReportFormOid && !itemName && (
                                                                <StatusListItemTooltip 
                                                                    title={
                                                                        <>
                                                                            <strong>Form Settings</strong>
                                                                        </>
                                                                    }
                                                                >
                                                                    <Paper>
                                                                        <Button
                                                                            color="inherit"
                                                                            sx={{
                                                                                minWidth: 0
                                                                            }}
                                                                            onClick={() => setSettingsDialogOpen(true)}
                                                                        >
                                                                            <FontAwesomeIcon icon={AppSettings.icons.configuration} size="lg"  />
                                                                        </Button>
                                                                    </Paper>
                                                                </StatusListItemTooltip>
                                                            )
                                                        }
                                                        {
                                                            studyEventOid && studyEventRepeat && !caseReportFormOid && !itemName && (
                                                                <StatusListItemTooltip 
                                                                    title={
                                                                        <>
                                                                            <strong>Update OpenClinica Event</strong>
                                                                        </>
                                                                    }
                                                                >
                                                                    <Paper>
                                                                        <Button
                                                                            color="inherit"
                                                                            sx={{
                                                                                minWidth: 0
                                                                            }}
                                                                            onClick={() => setEditEventDialogOpen(true)}
                                                                        >
                                                                            <FontAwesomeIcon icon={AppSettings.icons.edit} size="lg"  />
                                                                        </Button>
                                                                    </Paper>
                                                                </StatusListItemTooltip>
                                                            )
                                                        }
                                                        <StatusListItemTooltip 
                                                            title={
                                                                <>
                                                                    <strong>Data Last Updated</strong> <br />
                                                                    {sourceData?.date && DateTime.fromISO(sourceData.date).toFormat('dd/MM/yyyy tt')}
                                                                </>
                                                            }
                                                        >
                                                            <Paper>
                                                                <LoadingButton
                                                                    color="inherit"
                                                                    loading={loading || validating || refreshing}
                                                                    onClick={() => {
                                                                        queueStatusUpdate()
                                                                    }}
                                                                    sx={{
                                                                        minWidth: 0
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon={AppSettings.icons.refresh} size="lg"  />
                                                                </LoadingButton>
                                                            </Paper>
                                                        </StatusListItemTooltip>
                                                    </Box>
                                                </TitleBox>
                                                <Outlet />
                                                {
                                                    (!!studyEventOid || caseReportFormOid) && showLayout && (
                                                        <SourceDataNavigation />
                                                    )
                                                }
                                                {
                                                    studyEventOid && caseReportFormOid && !itemName && (
                                                        <SourceDataFormSettingsDialog 
                                                            formData={formSettings}
                                                            onClose={() => {
                                                                setSettingsDialogOpen(false);
                                                            }}
                                                            onSave={async (data) => {
                                                                setFormSettings(data);
                                                            }}
                                                            open={settingsDialogOpen}
                                                        />
                                                    )
                                                }
                                                {
                                                    studyEventOid && studyEventRepeat && !caseReportFormOid && !itemName && (
                                                        <SourceDataEventEditDialog 
                                                            trialId={trial?.id}
                                                            masterGroupId={participant?.masterGroupId}
                                                            coordinatingGroupId={participant?.coordinatingGroupId}
                                                            institutionId={participant?.institutionId}
                                                            participantId={participant?.id}
                                                            studyEventOid={studyEventOid}
                                                            studyEventRepeat={+studyEventRepeat}
                                                            sourceDataEvent={sourceData?.events?.[studyEventOid]?.[+studyEventRepeat]}
                                                            onClose={() => {
                                                                setEditEventDialogOpen(false);
                                                            }}
                                                            onSave={async () => {
                                                                //await sourceDataRefresh()
                                                            }}
                                                            open={editEventDialogOpen}
                                                        />
                                                    )
                                                }
                                            </Show>
                                        </SourceDataFormSettingsContext.Provider>
                                    </ParticipantSourceDataFormStatusesContext.Provider>
                                </ParticipantSourceDataEventStatusesContext.Provider>
                            </ConfigurationSourceDataContext.Provider>
                        </SourceDataFormContext.Provider>
                    </SourceDataContext.Provider>
                </ParticipantNotesAndQueriesContext.Provider>
            </ParticipantSourceDataFindingsContext.Provider>
        </>
    )
};

export default ParticipantSourceData;